import { Company, Doc, User, UserType } from '@app/models';
import { DN } from './parseKeyInfoSubject';

function digits(value: string | null | undefined): string {
  return String(value ?? '').replace(/\D/g, '');
}

export function verifyEcpMatchesContractParty(
  dn: DN,
  options: {
    userType: UserType;
    user: User;
    company: Company | null | undefined;
    contract: Doc;
  }
): { ok: true } | { ok: false; message: string } {
  const { userType, user, company, contract } = options;
  const certIin = digits(dn.iin);
  const certBin = dn.company ? digits(dn.company.bin) : '';

  if (userType === UserType.INDIVIDUAL) {
    const isRecipientUser = user.id === contract.recipientId;
    const isOwnerUser = user.id === contract.ownerId;
    if (!isRecipientUser && !isOwnerUser) {
      return {
        ok: false,
        message: 'Вы не являетесь стороной этого договора.',
      };
    }

    const authIin = digits(user.iin);
    if (!authIin) {
      return {
        ok: false,
        message: 'В профиле не указан ИИН. Заполните ИИН для подписания.',
      };
    }
    if (!certIin || certIin !== authIin) {
      return {
        ok: false,
        message: 'ИИН в сертификате ЭЦП не совпадает с ИИН в вашем аккаунте.',
      };
    }

    if (isRecipientUser) {
      const expected = digits(contract.recipient?.iin);
      if (expected && certIin !== expected) {
        return {
          ok: false,
          message: 'ЭЦП не соответствует получателю по договору.',
        };
      }
    } else if (isOwnerUser) {
      const expected = digits(contract.owner?.iin);
      if (expected && certIin !== expected) {
        return {
          ok: false,
          message: 'ЭЦП не соответствует инициатору договора.',
        };
      }
    }

    return { ok: true };
  }

  const isRecipientCompany = company?.id === contract.recipientCompanyId;
  const isOwnerCompany = company?.id === contract.companyId;
  if (!isRecipientCompany && !isOwnerCompany) {
    return {
      ok: false,
      message: 'Организация не является стороной этого договора.',
    };
  }

  const authBin = digits(company?.bin);
  if (!authBin) {
    return {
      ok: false,
      message: 'В профиле организации не указан БИН.',
    };
  }

  if (!dn.company || !certBin) {
    return {
      ok: false,
      message:
        'Для подписания от имени организации используйте сертификат юридического лица (с БИН).',
    };
  }

  if (certBin !== authBin) {
    return {
      ok: false,
      message:
        'БИН в сертификате ЭЦП не совпадает с БИН вашей организации в аккаунте.',
    };
  }

  if (isRecipientCompany) {
    const expected = digits(contract.recipientCompany?.bin);
    if (expected && certBin !== expected) {
      return {
        ok: false,
        message: 'ЭЦП не соответствует организации-получателю по договору.',
      };
    }
  } else if (isOwnerCompany) {
    const expected = digits(contract.company?.bin);
    if (expected && certBin !== expected) {
      return {
        ok: false,
        message: 'ЭЦП не соответствует организации-инициатору по договору.',
      };
    }
  }

  return { ok: true };
}
